/**
 * WCAG 1.4.13 (Content on Hover or Focus) for the primary nav dropdowns.
 *
 * The Impulse theme opens the ".site-nav--has-dropdown" menus purely on CSS
 * :hover. The theme already makes them dismissible/persistent for KEYBOARD use
 * (focusin/focusout + Escape on the nav items), but a mouse user who hovers a
 * menu has no way to dismiss it without moving the pointer away — Escape can't
 * undo a CSS :hover. That fails the "dismissible" requirement of 1.4.13.
 *
 * Hoverable (menu stays open while the pointer is over it) and persistent are
 * already satisfied structurally: each dropdown is nested inside the hovered
 * <li>, so hovering the dropdown keeps the parent :hover.
 *
 * This script adds the missing pieces:
 *   - pressing Escape while hovering a menu hides it (without moving the pointer
 *     or focus) until the pointer leaves and re-enters;
 *   - exposes each trigger's open/closed state via aria-haspopup / aria-expanded
 *     so assistive tech (and static a11y scanners) can see the popup pattern.
 * It layers on top of the theme and touches no third-party code.
 */
(function () {
    'use strict';

    var ITEM_SELECTOR = '.site-nav--has-dropdown';
    var TRIGGER_SELECTOR = '.site-nav__link--has-dropdown, .site-nav__link';
    var DISMISSED_CLASS = 'nav-dropdown-dismissed';
    var THEME_OPEN_CLASS = 'is-focused'; // theme adds this on hover AND focus

    // CSS that force-hides the dropdown of an Escape-dismissed item. It is
    // unconditional (not scoped to :hover) so it overrides BOTH ways the theme
    // reveals a menu — the :hover rule and the .is-focused rule — including when
    // the pointer is over the submenu itself. Injected here so it doesn't depend
    // on the SCSS build pipeline.
    function injectStyles() {
        if (document.getElementById('nav-dropdown-a11y-styles')) return;
        var style = document.createElement('style');
        style.id = 'nav-dropdown-a11y-styles';
        style.textContent =
            '.site-nav--has-dropdown.' +
            DISMISSED_CLASS +
            ' .site-nav__dropdown{' +
            'opacity:0 !important;visibility:hidden !important;pointer-events:none !important;}';
        document.head.appendChild(style);
    }

    function getTrigger(item) {
        return item.querySelector(TRIGGER_SELECTOR);
    }

    // Mirror the item's visible state onto the trigger's aria-expanded. The menu
    // is open when the theme's is-focused class is present and it hasn't been
    // Escape-dismissed.
    function syncExpanded(item) {
        var trigger = getTrigger(item);
        if (!trigger) return;
        var open =
            item.classList.contains(THEME_OPEN_CLASS) && !item.classList.contains(DISMISSED_CLASS);
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function dismissHoveredMenus() {
        var hovered = document.querySelectorAll(ITEM_SELECTOR + ':hover');
        var dismissedAny = false;

        Array.prototype.forEach.call(hovered, function (item) {
            if (item.classList.contains(DISMISSED_CLASS)) return; // already dismissed
            item.classList.add(DISMISSED_CLASS);
            syncExpanded(item);
            dismissedAny = true;
        });

        return dismissedAny;
    }

    // Re-arm a dismissed menu only when the pointer or keyboard focus enters the
    // item AGAIN. Using enter (not leave) is deliberate: hiding the submenu under
    // the pointer fires an immediate mouseleave, which must NOT reopen the menu.
    function rearm(e) {
        var item = e.currentTarget;
        if (!item.classList.contains(DISMISSED_CLASS)) return;
        item.classList.remove(DISMISSED_CLASS);
        syncExpanded(item);
    }

    function onKeydown(e) {
        if (e.key !== 'Escape' && e.keyCode !== 27) return;
        // Only handle the hover case here; the theme handles Escape when a nav item
        // has keyboard focus (and returns focus to the trigger).
        dismissHoveredMenus();
    }

    function setupAria() {
        var items = document.querySelectorAll(ITEM_SELECTOR);
        Array.prototype.forEach.call(items, function (item) {
            var trigger = getTrigger(item);
            if (trigger) {
                trigger.setAttribute('aria-haspopup', 'true');
                syncExpanded(item);
            }
            // Re-open a previously dismissed menu when the user deliberately
            // returns to it (pointer re-enters or keyboard focus lands on it).
            item.addEventListener('mouseenter', rearm);
            item.addEventListener('focusin', rearm);
        });

        // Keep aria-expanded in sync as the theme toggles is-focused on hover/focus.
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (m) {
                var item = m.target.closest ? m.target.closest(ITEM_SELECTOR) : null;
                if (item) syncExpanded(item);
            });
        });
        Array.prototype.forEach.call(items, function (item) {
            observer.observe(item, { attributes: true, attributeFilter: ['class'] });
        });
    }

    function init() {
        injectStyles();
        setupAria();
        document.addEventListener('keydown', onKeydown);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
